import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getProjects } from '../services/markdownProjectService';

const LabResultsContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-lavender);
  }
`;

const IntroText = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: var(--spacing-xl);
  flex-wrap: wrap;
  gap: var(--spacing-xs);
`;

const FilterButton = styled.button`
  background-color: ${props => props.isActive ? 'var(--accent-lavender)' : 'transparent'};
  color: ${props => props.isActive ? 'white' : 'var(--text)'};
  border: 2px solid var(--accent-lavender);
  padding: 0.5rem 1.25rem;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0 0.25rem;
  
  &:hover {
    background-color: ${props => props.isActive ? 'var(--accent-lavender)' : 'rgba(123, 123, 243, 0.1)'};
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
`;

const CardContent = styled.div`
  padding: var(--spacing-lg);
`;

const CardCategory = styled.span`
  display: inline-block;
  background-color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': return 'rgba(62, 221, 226, 0.1)';
      case 'Tourism Development': return 'rgba(123, 123, 243, 0.1)';
      case 'Digital Tools': return 'rgba(123, 211, 244, 0.1)';
      default: return 'rgba(62, 221, 226, 0.1)';
    }
  }};
  color: ${props => {
    switch(props.category) {
      case 'Travel Marketing': return 'var(--accent-turquoise)';
      case 'Tourism Development': return 'var(--accent-lavender)';
      case 'Digital Tools': return 'var(--accent-blue)';
      default: return 'var(--accent-turquoise)';
    }
  }};
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin: var(--spacing-sm) 0;
  color: var(--text);
`;

const CardDescription = styled.p`
  color: var(--text-light);
  margin-bottom: var(--spacing-md);
`;

const CardStat = styled.div`
  font-weight: 600;
  color: var(--accent-lavender);
  margin: var(--spacing-sm) 0;
`;

const CardLink = styled(Link)`
  display: inline-block;
  color: var(--accent-turquoise);
  font-weight: 600;
  text-decoration: none;
  margin-top: var(--spacing-sm);
  
  &:hover {
    text-decoration: underline;
  }
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xxl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const CardButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const LabResults = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const projectsData = await getProjects();
        setProjects(projectsData);
        setLoading(false);
      } catch (err) {
        setError('Failed to load projects. Please try again later.');
        setLoading(false);
        console.error('Error fetching projects:', err);
      }
    };

    fetchProjects();
  }, []);

  // Set initial filter from URL on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const categoryParam = urlParams.get('category');
    const hashCategory = location.hash.replace('#', '');
    
    // Map URL params to display names
    const urlToDisplayMap = {
      'all': 'All',
      'travel-marketing': 'Travel Marketing',
      'tourism-development': 'Tourism Development',
      'digital-tools': 'Digital Tools'
    };
    
    if (categoryParam && urlToDisplayMap[categoryParam]) {
      setActiveFilter(urlToDisplayMap[categoryParam]);
    } else if (hashCategory && urlToDisplayMap[hashCategory]) {
      setActiveFilter(urlToDisplayMap[hashCategory]);
    }
  }, [location]);

  // Handle filter change and update URL
  const handleFilterChange = (displayName) => {
    setActiveFilter(displayName);
    
    // Map display names back to URL params
    const displayToUrlMap = {
      'All': 'all',
      'Travel Marketing': 'travel-marketing',
      'Tourism Development': 'tourism-development',
      'Digital Tools': 'digital-tools'
    };
    
    const urlParam = displayToUrlMap[displayName];
    const newUrl = urlParam === 'all' 
      ? '/projects' 
      : `/projects?category=${urlParam}`;
    
    navigate(newUrl, { replace: true });
  };

  // Map category IDs to display names
  const categoryDisplayMap = {
    'travel-marketing': 'Travel Marketing',
    'tourism-development': 'Tourism Development', 
    'digital-tools': 'Digital Tools'
  };
  
  const filteredCaseStudies = activeFilter === 'All' 
    ? projects
    : projects.filter(project => {
        const displayName = categoryDisplayMap[project.category];
        return displayName === activeFilter;
      });
  
  return (
    <LabResultsContainer>
      <Container>
        <PageTitle>Our <span>Projects</span></PageTitle>
        <IntroText>See what happens when we tinker—real wins from the field.</IntroText>
        
        <FilterContainer>
          <FilterButton 
            isActive={activeFilter === 'All'} 
            onClick={() => handleFilterChange('All')}
          >
            All
          </FilterButton>
          <FilterButton 
            isActive={activeFilter === 'Travel Marketing'} 
            onClick={() => handleFilterChange('Travel Marketing')}
          >
            Travel Marketing
          </FilterButton>
          <FilterButton 
            isActive={activeFilter === 'Tourism Development'} 
            onClick={() => handleFilterChange('Tourism Development')}
          >
            Tourism Development
          </FilterButton>
          <FilterButton 
            isActive={activeFilter === 'Digital Tools'} 
            onClick={() => handleFilterChange('Digital Tools')}
          >
            Digital Tools
          </FilterButton>
        </FilterContainer>
        
        {loading ? (
          <div>Loading projects...</div>
        ) : error ? (
          <div>{error}</div>
        ) : (
          <CardGrid>
            {filteredCaseStudies.map(project => (
              <Card key={project.id}>
                <Link to={`/projects/${project.slug || project.id}`}>
                  <CardImage style={{ backgroundImage: `url(${project.imageUrl})` }} />
                </Link>
                <CardContent>
                  <CardCategory category={categoryDisplayMap[project.category] || project.category}>
                    {categoryDisplayMap[project.category] || project.category}
                  </CardCategory>
                  <CardTitle>{project.title}</CardTitle>
                  <CardDescription>{project.summary}</CardDescription>
                  <CardButton to={`/projects/${project.slug || project.id}`}>
                    View Case Study
                  </CardButton>
                </CardContent>
              </Card>
            ))}
          </CardGrid>
        )}
        
        <CTASection>
          <CTATitle>Want Results Like These?</CTATitle>
          <p>Let's discuss how we can achieve similar outcomes for your travel business.</p>
          <CTAButton to="/contact">Get in Touch</CTAButton>
        </CTASection>
      </Container>
    </LabResultsContainer>
  );
};

export default LabResults; 